/**
 * Helpers for the bilingual description fields used across V2 admin
 * forms (property + room).
 *
 * The DB column shape is `description_translations` JSONB, with the
 * convention `{ en: string, de: string }`. We keep the legacy
 * single-language `description` column mirrored to the EN value for
 * back-compat with anything still reading it.
 *
 * Lives outside BilingualTextarea.tsx so that file can stay a pure
 * components module (react-refresh / Fast Refresh requirement).
 */

export type BilingualValue = { en: string; de: string };

/**
 * Build the JSONB payload for `description_translations`. Trims and
 * omits empty values so the column doesn't accumulate `{en: '', de: ''}`.
 */
export function bilingualToTranslationsJsonb(
  v: BilingualValue
): Record<string, string> {
  const out: Record<string, string> = {};
  const en = v.en.trim();
  const de = v.de.trim();
  if (en) out.en = en;
  if (de) out.de = de;
  return out;
}

/**
 * Coerce anything stored in a description field to text a form can show.
 *
 * `properties.description` is a text column, but rows written before the
 * bilingual split — and anything that ever round-tripped a `{ en, de }` object
 * through it — can hold a JSON object instead of a string. Bound straight to a
 * textarea, that renders as the literal "[object Object]", which is then
 * saveable: the editor sees nonsense, cannot tell it from real copy, and one
 * Save writes it back as the property's public description.
 *
 * So: a string passes through; a bilingual-shaped object yields its English
 * text; anything else yields empty. Empty is the right failure — an editor
 * facing a blank field writes the description, while one facing
 * "[object Object]" has no idea what happened or what it replaced.
 */
export function asDescriptionText(value: unknown): string {
  if (typeof value === "string") return value;
  if (value && typeof value === "object") {
    const en = (value as Record<string, unknown>).en;
    if (typeof en === "string") return en;
  }
  return "";
}

/**
 * Read the bilingual value from a row's
 *   description (legacy single-language column) +
 *   description_translations (JSONB)
 *
 * Priority for EN: description_translations.en → description (legacy).
 * Priority for DE: description_translations.de → empty.
 */
export function bilingualFromRow(row: {
  description?: string | null;
  description_translations?: unknown;
}): BilingualValue {
  const t = (row.description_translations ?? null) as Record<
    string,
    unknown
  > | null;
  const en =
    typeof t?.en === "string" && t.en.trim()
      ? (t.en as string)
      : // Legacy column, and not necessarily a string — see asDescriptionText.
        asDescriptionText(row.description);
  const de = typeof t?.de === "string" ? (t.de as string) : "";
  return { en, de };
}
